import { trackNavigatorEvent } from '../../lib/analytics/navigatorEvents'
import { APPLICABILITY_LABELS, evaluateApplicability, isActionable } from '../../lib/regulations/applicability'
import { buildChecklist, buildChecklistExport, checklistToPlainText, productCtas } from '../../lib/regulations/checklist'
import { filterDeadlines } from '../../lib/regulations/deadlines'
import { formatDeadlineDate, formatIsoDate, todayIsoInUae } from '../../lib/regulations/dates'
import { filterRecords, paginate } from '../../lib/regulations/filtering'
import { submitNavigatorLead } from '../../lib/lead-capture/navigatorAdapter'
import { buildTimeline, filterTimeline, groupTimelineByYear } from '../../lib/regulations/timeline'
import { APPLICABILITY_RULES, emptyProfile, uaePresence } from '../../data/regulations/applicabilityRules'
import { PUBLISHED_RECORDS } from '../../data/regulations/records'
import {
  ACTIVITY_OPTIONS,
  CATEGORY_LABELS,
  EMIRATE_OPTIONS,
  INSTRUMENT_LABELS,
  JURISDICTION_LABELS,
  NAVIGATOR_BASE_PATH,
  OBLIGATION_LABELS,
  SECTOR_OPTIONS,
  STATUS_LABELS,
  TIMELINE_KIND_LABELS,
} from '../../data/regulations/taxonomy'
import type { CompanyProfile, RegisterQuery, TimelineKind } from '../../data/regulations/types'
import { renderChecklistPdf } from './pdf'
import { clearSession, loadSession, saveSession, type NavigatorSession } from './storage'

const BASE = NAVIGATOR_BASE_PATH

function h(tag: string, attrs: Record<string, string | undefined> = {}, children: (Node | string)[] = []): HTMLElement {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null) continue
    if (key === 'class') node.className = value
    else node.setAttribute(key, value)
  }
  for (const child of children) node.append(child)
  return node
}

function badge(className: string, text: string): HTMLElement {
  return h('span', { class: `rn-badge ${className}` }, [text])
}

function hideSsr(): void {
  const ssr = document.getElementById('reg-ssr')
  if (ssr) ssr.hidden = true
}

function recordLink(slug: string, label: string): HTMLAnchorElement {
  const link = h('a', { href: `${BASE}/${slug}` }, [label]) as HTMLAnchorElement
  link.addEventListener('click', () => trackNavigatorEvent('regulatory_record_open', { slug }))
  return link
}

function mountTimeline(root: HTMLElement): void {
  hideSsr()
  const today = todayIsoInUae()
  const state = { search: '', period: 'all', category: '', jurisdiction: '', kind: '' }
  const events = buildTimeline(PUBLISHED_RECORDS, today)

  const search = h('input', { type: 'search', id: 'tl-search', placeholder: 'Search events' }) as HTMLInputElement
  const period = select('tl-period', [
    ['all', 'All dates'],
    ['past', 'Previous years'],
    ['current', 'Current year'],
    ['future', 'Future dates'],
  ])
  const category = select('tl-cat', [['', 'All categories'], ...Object.entries(CATEGORY_LABELS)])
  const jurisdiction = select('tl-jur', [['', 'All jurisdictions'], ...Object.entries(JURISDICTION_LABELS)])
  const kind = select('tl-kind', [['', 'All event types'], ...Object.entries(TIMELINE_KIND_LABELS)])
  const list = h('div', { id: 'tl-list' })
  const count = h('p', { id: 'tl-count' })

  const redraw = () => {
    const filtered = filterTimeline(events, {
      search: state.search,
      period: state.period as 'all' | 'past' | 'current' | 'future',
      categories: state.category ? [state.category] : [],
      jurisdictions: state.jurisdiction ? [state.jurisdiction] : [],
      kinds: state.kind ? [state.kind as TimelineKind] : [],
    }, today)
    count.textContent = `${filtered.length} event${filtered.length === 1 ? '' : 's'}`
    list.replaceChildren()
    if (!filtered.length) {
      list.append(h('div', { class: 'rn-empty' }, ['No events match these filters. Clear a filter or try another year group.']))
      return
    }
    for (const group of groupTimelineByYear(filtered)) {
      list.append(h('h2', { class: 'rn-year' }, [group.year]))
      const rail = h('div', { class: 'rn-timeline' })
      for (const event of group.events) {
        const article = h('article', { class: 'rn-event' })
        article.append(
          badge(`rn-badge--${event.kind}`, TIMELINE_KIND_LABELS[event.kind]),
          badge(`rn-badge--${event.status}`, STATUS_LABELS[event.status]),
          h('h3', {}, [event.date ? formatIsoDate(event.date) : 'No confirmed date identified']),
          h('p', {}, [recordLink(event.slug, event.shortTitle)]),
          h('p', {}, [event.label]),
          h('p', {}, [event.authority]),
        )
        if (event.notes) article.append(h('p', {}, [event.notes]))
        const open = h('a', { href: `${BASE}/${event.slug}`, class: 'btn btn--secondary btn--sm' }, ['Open record']) as HTMLAnchorElement
        open.addEventListener('click', () => trackNavigatorEvent('timeline_event_open', { slug: event.slug, kind: event.kind }))
        article.append(h('div', { class: 'rn-actions' }, [open]))
        rail.append(article)
      }
      list.append(rail)
    }
  }

  const onFilter = () => {
    state.search = search.value
    state.period = period.value
    state.category = category.value
    state.jurisdiction = jurisdiction.value
    state.kind = kind.value
    trackNavigatorEvent('regulatory_filter_applied', {
      period: state.period,
      hasCategory: Boolean(state.category),
      hasJurisdiction: Boolean(state.jurisdiction),
      resultHint: 'timeline',
    })
    redraw()
  }
  search.addEventListener('input', () => {
    state.search = search.value
    trackNavigatorEvent('regulatory_search', { surface: 'timeline', queryLength: search.value.trim().length })
    redraw()
  })
  ;[period, category, jurisdiction, kind].forEach((control) => control.addEventListener('change', onFilter))

  root.append(
    h('div', { class: 'rn-filters' }, [
      field('Search', search),
      field('Period', period),
      field('Category', category),
      field('Jurisdiction', jurisdiction),
      field('Event type', kind),
    ]),
    count,
    list,
  )
  redraw()
}

function mountRegister(root: HTMLElement): void {
  hideSsr()
  const query: RegisterQuery = { sort: 'title', sortDirection: 'asc' }
  let page = 1
  const search = h('input', { type: 'search', placeholder: 'Search title, authority or topic', id: 'reg-search' }) as HTMLInputElement
  const jurisdiction = select('reg-jur', [['', 'All jurisdictions'], ...Object.entries(JURISDICTION_LABELS)])
  const category = select('reg-cat', [['', 'All categories'], ...Object.entries(CATEGORY_LABELS)])
  const instrument = select('reg-type', [['', 'All instrument types'], ...Object.entries(INSTRUMENT_LABELS)])
  const status = select('reg-status', [['', 'All statuses'], ...Object.entries(STATUS_LABELS)])
  const listing = select('reg-list', [['', 'Listed and unlisted'], ['listed', 'Relevant to listed companies'], ['unlisted', 'Relevant to unlisted companies']])
  const financial = select('reg-fin', [['', 'Financial and non-financial'], ['financial', 'Financial entities'], ['non_financial', 'Non-financial entities']])
  const sector = select('reg-sec', [['', 'All sectors'], ...SECTOR_OPTIONS.map((item) => [item.id, item.label] as [string, string])])
  const establishment = select('reg-est', [['', 'Mainland and free zone'], ['mainland', 'Mainland context'], ['free_zone', 'Free-zone context']])
  const obligation = select('reg-ob', [['', 'Any obligation level'], ['mandatory', 'Mandatory'], ['conditional', 'Conditional'], ['voluntary', 'Voluntary'], ['informational', 'Informational']])
  const confirmed = h('input', { type: 'checkbox', id: 'reg-confirmed' }) as HTMLInputElement
  const sort = select('reg-sort', [['title', 'Title'], ['publication', 'Publication date'], ['effective', 'Effective date'], ['authority', 'Authority'], ['status', 'Status']])
  const count = h('p')
  const list = h('div')
  const pager = h('div', { class: 'rn-pager' })

  const read = () => {
    query.search = search.value
    query.jurisdictions = jurisdiction.value ? [jurisdiction.value as never] : []
    query.categories = category.value ? [category.value as never] : []
    query.instrumentTypes = instrument.value ? [instrument.value as never] : []
    query.statuses = status.value ? [status.value as never] : []
    query.listing = listing.value as RegisterQuery['listing']
    query.financial = financial.value as RegisterQuery['financial']
    query.sector = sector.value
    query.establishment = establishment.value as RegisterQuery['establishment']
    query.obligation = obligation.value as RegisterQuery['obligation']
    query.confirmedDeadlinesOnly = confirmed.checked
    query.sort = sort.value as RegisterQuery['sort']
  }

  const redraw = () => {
    read()
    const filtered = filterRecords(PUBLISHED_RECORDS, query)
    const view = paginate(filtered, page, 8)
    page = view.page
    count.textContent = `${view.total} record${view.total === 1 ? '' : 's'}`
    list.replaceChildren()
    if (!view.total) {
      list.append(h('div', { class: 'rn-empty' }, ['Nothing matches. Clear filters or use a broader search.']))
    }
    for (const record of view.items) {
      const confirmedDate = record.deadlines.find((deadline) => deadline.certainty === 'confirmed' && deadline.date)
      const card = h('article', { class: 'rn-card' })
      card.append(
        badge(`rn-badge--${record.status}`, STATUS_LABELS[record.status]),
        h('h3', {}, [recordLink(record.slug, record.title)]),
        h('p', {}, [`${record.authority}`]),
        h('p', {}, [`${record.jurisdiction.map((id) => JURISDICTION_LABELS[id]).join(', ')} · ${INSTRUMENT_LABELS[record.instrumentType]} · ${record.categories.map((id) => CATEGORY_LABELS[id]).join(', ')}`]),
        h('p', {}, [record.applicabilitySummary]),
        h('p', {}, [`Published: ${record.publicationDate ? formatIsoDate(record.publicationDate) : 'No confirmed date identified'}. Effective: ${record.effectiveDate ? formatIsoDate(record.effectiveDate) : 'No confirmed effective date identified'}.`]),
        h('p', {}, [confirmedDate ? `Confirmed date: ${formatIsoDate(confirmedDate.date)} — ${confirmedDate.label}` : 'No confirmed deadline identified']),
        h('p', {}, [
          'Source: ',
          h('a', { href: record.officialSources[0]?.url || '#', rel: 'noopener noreferrer' }, [record.officialSources[0]?.title || 'Official source']),
        ]),
        h('p', {}, [`Last reviewed ${formatIsoDate(record.lastReviewedDate)}`]),
      )
      list.append(card)
    }
    pager.replaceChildren()
    if (view.pages > 1) {
      const prev = h('button', { type: 'button', class: 'btn btn--secondary btn--sm' }, ['Previous']) as HTMLButtonElement
      const next = h('button', { type: 'button', class: 'btn btn--secondary btn--sm' }, ['Next']) as HTMLButtonElement
      prev.disabled = view.page <= 1
      next.disabled = view.page >= view.pages
      prev.addEventListener('click', () => { page -= 1; redraw() })
      next.addEventListener('click', () => { page += 1; redraw() })
      pager.append(prev, h('span', {}, [`Page ${view.page} of ${view.pages}`]), next)
    }
  }

  const onChange = () => {
    page = 1
    trackNavigatorEvent('regulatory_filter_applied', { surface: 'register' })
    redraw()
  }
  search.addEventListener('input', () => {
    page = 1
    trackNavigatorEvent('regulatory_search', { surface: 'register', queryLength: search.value.trim().length })
    redraw()
  })
  ;[jurisdiction, category, instrument, status, listing, financial, sector, establishment, obligation, sort].forEach((control) => control.addEventListener('change', onChange))
  confirmed.addEventListener('change', onChange)

  const clear = h('button', { type: 'button', class: 'btn btn--secondary btn--sm' }, ['Clear all filters']) as HTMLButtonElement
  clear.addEventListener('click', () => {
    search.value = ''
    ;[jurisdiction, category, instrument, status, listing, financial, sector, establishment, obligation].forEach((control) => { control.value = '' })
    sort.value = 'title'
    confirmed.checked = false
    page = 1
    redraw()
  })

  const filters = h('details', { class: 'rn-filters', open: 'open' })
  filters.append(
    h('summary', {}, ['Filters']),
    field('Jurisdiction', jurisdiction),
    field('Category', category),
    field('Instrument type', instrument),
    field('Status', status),
    field('Listing', listing),
    field('Financial / non-financial', financial),
    field('Sector', sector),
    field('Mainland / free zone', establishment),
    field('Obligation', obligation),
    h('label', {}, [confirmed, ' Confirmed dated deadlines only']),
    field('Sort', sort),
    clear,
  )
  root.append(h('div', { class: 'rn-layout' }, [
    filters,
    h('div', {}, [field('Search', search), h('div', { class: 'rn-toolbar' }, [count]), list, pager]),
  ]))
  redraw()
}

function mountDeadlines(root: HTMLElement): void {
  hideSsr()
  const today = todayIsoInUae()
  const windowSelect = select('dl-window', [['all', 'All dated items'], ['30', 'Next 30 days'], ['90', 'Next 90 days'], ['365', 'Next 12 months'], ['custom', 'Custom range']])
  const start = h('input', { type: 'date', id: 'dl-start' }) as HTMLInputElement
  const end = h('input', { type: 'date', id: 'dl-end' }) as HTMLInputElement
  const authority = h('input', { type: 'search', id: 'dl-auth', placeholder: 'Regulator or authority' }) as HTMLInputElement
  const jurisdiction = select('dl-jur', [['', 'All jurisdictions'], ...Object.entries(JURISDICTION_LABELS)])
  const category = select('dl-cat', [['', 'All categories'], ...Object.entries(CATEGORY_LABELS)])
  const confirmed = h('input', { type: 'checkbox', id: 'dl-confirmed' }) as HTMLInputElement
  const profileNote = h('p')
  const datedHost = h('div')
  const undatedHost = h('div')

  const redraw = () => {
    const session = loadSession()
    const recordIds = session ? session.items.map((item) => item.recordId) : undefined
    profileNote.textContent = session
      ? 'Showing dates for records on your current checklist. Clear the checklist to see the full calendar.'
      : 'No company profile is stored in this tab, so the full calendar is shown.'
    const result = filterDeadlines(PUBLISHED_RECORDS, {
      window: windowSelect.value as 'all' | '30' | '90' | '365' | 'custom',
      start: windowSelect.value === 'custom' ? start.value : undefined,
      end: windowSelect.value === 'custom' ? end.value : undefined,
      authority: authority.value,
      jurisdictions: jurisdiction.value ? [jurisdiction.value as never] : [],
      categories: category.value ? [category.value as never] : [],
      confirmedOnly: confirmed.checked,
      recordIds,
      today,
    })
    datedHost.replaceChildren(h('h2', {}, ['In the selected window']))
    if (!result.dated.length) datedHost.append(h('div', { class: 'rn-empty' }, ['No dated item falls in this window.']))
    for (const item of result.dated) {
      datedHost.append(h('article', { class: 'rn-card' }, [
        badge(`rn-badge--${item.certainty === 'confirmed' ? 'in_force' : 'expected'}`, item.certainty),
        h('h3', {}, [formatDeadlineDate(item.date)]),
        h('p', {}, [recordLink(item.slug, item.shortTitle)]),
        h('p', {}, [`${item.type.replace('_', ' ')} — ${item.label}`]),
        h('p', {}, [item.notes || item.authority]),
        h('p', {}, ['Source: ', h('a', { href: item.sourceUrl || '#', rel: 'noopener noreferrer' }, [item.sourceUrl || 'See record'])]),
      ]))
    }
    undatedHost.replaceChildren(h('h2', {}, ['No confirmed deadline identified']))
    undatedHost.append(h('p', {}, ['These duties are real in the source text but have no calendar day. They are not placed inside the date window.']))
    for (const item of result.undated) {
      undatedHost.append(h('article', { class: 'rn-card' }, [
        badge('rn-badge--expected', 'Undated'),
        h('h3', {}, [recordLink(item.slug, item.shortTitle)]),
        h('p', {}, [item.label]),
      ]))
    }
  }

  const changed = () => {
    trackNavigatorEvent('deadline_filter_applied', { window: windowSelect.value, confirmedOnly: confirmed.checked })
    redraw()
  }
  ;[windowSelect, start, end, jurisdiction, category, confirmed].forEach((control) => control.addEventListener('change', changed))
  authority.addEventListener('input', changed)
  root.append(
    profileNote,
    h('div', { class: 'rn-filters' }, [
      field('Window', windowSelect),
      field('Custom start', start),
      field('Custom end', end),
      field('Authority', authority),
      field('Jurisdiction', jurisdiction),
      field('Category', category),
      h('label', {}, [confirmed, ' Confirmed items only (hides proposed and expected)']),
    ]),
    datedHost,
    undatedHost,
  )
  redraw()
}

function mountExplorer(root: HTMLElement): void {
  hideSsr()
  trackNavigatorEvent('applicability_wizard_start')
  const profile = loadSession()?.profile || emptyProfile()
  let step = 0
  const steps = ['Where you operate', 'What you do', 'Listing and finance', 'Markets and data', 'Results']

  const host = h('div', { class: 'rn-wizard' })
  const render = () => {
    host.replaceChildren()
    host.append(
      h('div', { class: 'rn-stepper', role: 'list' }, steps.map((label, index) => {
        const state = index < step ? 'is-done' : index === step ? 'is-on' : 'is-todo'
        return h('div', { class: `rn-stepper__item ${state}`, role: 'listitem' }, [
          h('span', { class: 'rn-stepper__index', 'aria-hidden': 'true' }, [index < step ? '✓' : String(index + 1)]),
          h('span', { class: 'rn-stepper__label' }, [label]),
        ])
      })),
      h('p', { class: 'rn-wizard__progress' }, [`Step ${step + 1} of ${steps.length} · ${steps[step]}`]),
    )

    const panel = h('div', { class: 'rn-wizard__panel' })
    if (step === 0) {
      panel.append(
        emirateStep(profile),
        choiceStep('Establishment', profile, 'establishment', [
          ['mainland', 'Mainland'],
          ['free_zone', 'Free zone'],
          ['both', 'Both'],
          ['not_sure', 'Not sure'],
        ]),
      )
    }
    if (step === 1) {
      panel.append(
        choiceStep('Sector', profile, 'sector', SECTOR_OPTIONS.map((item) => [item.id, item.label])),
        checksStep('Activities', profile.activities, ACTIVITY_OPTIONS.map((item) => [item.id, item.label])),
        choiceStep('Company size band', profile, 'size', [
          ['small', 'Small'],
          ['medium', 'Medium'],
          ['large', 'Large'],
          ['not_sure', 'Not sure'],
        ]),
        h('p', { class: 'rn-hint' }, [
          'Size is stored with your answers and is not used as a legal threshold. None of the verified instruments uses headcount.',
        ]),
      )
    }
    if (step === 2) {
      panel.append(
        choiceStep('Listing', profile, 'listing', [
          ['unlisted', 'Unlisted'],
          ['adx', 'ADX'],
          ['dfm', 'DFM'],
          ['both_exchanges', 'ADX and DFM'],
          ['other_exchange', 'Another exchange'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Financial institution?', profile, 'financial', [
          ['non_financial', 'Non-financial company'],
          ['bank', 'Bank'],
          ['insurer', 'Insurer'],
          ['other_financial', 'Other financial institution'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Preparing for an IPO?', profile, 'ipo', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
      )
    }
    if (step === 3) {
      panel.append(
        choiceStep('Export to the EU?', profile, 'exportEu', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Customers request emissions or ESG data?', profile, 'customerRequests', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Lenders or investors request sustainability data?', profile, 'lenderRequests', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('More than one facility?', profile, 'multiFacility', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Received an official reporting notice?', profile, 'officialNotice', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
        choiceStep('Already prepare a GHG inventory?', profile, 'ghgInventory', [
          ['no', 'No'],
          ['yes', 'Yes'],
          ['not_sure', 'Not sure'],
        ]),
      )
    }
    if (step === 4) panel.append(renderResults(profile))
    host.append(panel)

    const back = h('button', { type: 'button', class: 'btn btn--secondary' }, ['Back']) as HTMLButtonElement
    const next = h('button', { type: 'button', class: 'btn btn--primary' }, [
      step === 3 ? 'See results' : step === 4 ? 'Generate checklist' : 'Continue',
    ]) as HTMLButtonElement
    back.disabled = step === 0
    back.addEventListener('click', () => {
      step -= 1
      render()
      root.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    next.addEventListener('click', () => {
      if (step < 4) {
        step += 1
        if (step === 4) trackNavigatorEvent('applicability_wizard_complete', { presence: uaePresence(profile) })
        render()
        root.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      const matches = evaluateApplicability(PUBLISHED_RECORDS, profile)
      const items = buildChecklist(PUBLISHED_RECORDS, matches)
      saveSession({ profile, items, hiddenIds: [], completedIds: [], generatedAt: todayIsoInUae() })
      trackNavigatorEvent('checklist_generated', { items: items.length })
      window.location.href = `${BASE}/checklist`
    })
    host.append(h('div', { class: 'rn-actions rn-wizard__actions' }, [back, next]))
  }

  function emirateStep(current: CompanyProfile): HTMLElement {
    const box = h('fieldset', { class: 'rn-field rn-field--choice' })
    box.append(h('legend', {}, ['Emirates of operation']))
    const grid = h('div', { class: 'rn-choice-grid' })
    for (const option of EMIRATE_OPTIONS) {
      const input = h('input', { type: 'checkbox' }) as HTMLInputElement
      input.checked = current.emirates.includes(option.id)
      input.addEventListener('change', () => {
        const set = new Set(current.emirates)
        if (input.checked) set.add(option.id)
        else set.delete(option.id)
        if (option.id === 'none' && input.checked) {
          current.emirates = ['none']
        } else if (option.id !== 'none' && input.checked) {
          set.delete('none')
          current.emirates = [...set]
        } else current.emirates = [...set]
        render()
      })
      const label = h('label', { class: `rn-choice${input.checked ? ' is-selected' : ''}` }, [
        input,
        h('span', {}, [option.label]),
      ])
      grid.append(label)
    }
    box.append(grid)
    return box
  }

  function checksStep(legend: string, selected: string[], options: [string, string][]): HTMLElement {
    const box = h('fieldset', { class: 'rn-field rn-field--choice' })
    box.append(h('legend', {}, [legend]))
    const grid = h('div', { class: 'rn-choice-grid' })
    for (const [id, labelText] of options) {
      const input = h('input', { type: 'checkbox' }) as HTMLInputElement
      input.checked = selected.includes(id)
      input.addEventListener('change', () => {
        const set = new Set(profile.activities)
        if (input.checked) set.add(id)
        else set.delete(id)
        profile.activities = [...set]
        render()
      })
      grid.append(
        h('label', { class: `rn-choice${input.checked ? ' is-selected' : ''}` }, [
          input,
          h('span', {}, [labelText]),
        ]),
      )
    }
    box.append(grid)
    return box
  }

  root.append(host)
  render()
  void APPLICABILITY_RULES
}

function choiceStep(legend: string, profile: CompanyProfile, key: keyof CompanyProfile, options: [string, string][]): HTMLElement {
  const box = h('fieldset', { class: 'rn-field rn-field--choice' })
  const name = `choice-${key}`
  box.append(h('legend', {}, [legend]))
  const grid = h('div', { class: 'rn-choice-grid' })
  for (const [id, labelText] of options) {
    const input = h('input', { type: 'radio', name }) as HTMLInputElement
    input.checked = profile[key] === id
    const label = h('label', { class: `rn-choice${input.checked ? ' is-selected' : ''}` }, [
      input,
      h('span', {}, [labelText]),
    ])
    input.addEventListener('change', () => {
      ;(profile as unknown as Record<string, string>)[key] = id
      grid.querySelectorAll('.rn-choice').forEach((node) => node.classList.remove('is-selected'))
      label.classList.add('is-selected')
    })
    grid.append(label)
  }
  box.append(grid)
  return box
}

function renderResults(profile: CompanyProfile): HTMLElement {
  const matches = evaluateApplicability(PUBLISHED_RECORDS, profile)
  const shown = matches.filter((match) => isActionable(match.state))
  const hidden = matches.length - shown.length
  const wrap = h('div', { class: 'rn-results' })
  wrap.append(
    h('div', { class: 'rn-results__summary' }, [
      h('strong', {}, [String(shown.length)]),
      h('span', {}, [
        ` record${shown.length === 1 ? '' : 's'} triggered by these answers. ${hidden} have no trigger identified — that is not a finding of non-applicability.`,
      ]),
    ]),
  )
  wrap.append(
    h('aside', { class: 'rn-card rn-legal-note' }, [
      h('h3', {}, ['This is not a legal determination']),
      h('p', {}, [
        'If you need to know whether a rule applies, ask qualified UAE counsel. Ecops carbon-accounting software does not answer that question.',
      ]),
    ]),
  )
  for (const cta of productCtas(profile, matches)) {
    wrap.append(
      h('aside', { class: 'rn-card rn-promo' }, [
        h('h3', {}, [cta.title]),
        h('p', {}, [cta.body]),
        (() => {
          const link = h('a', { href: cta.href, class: 'btn btn--secondary btn--sm' }, ['Learn more']) as HTMLAnchorElement
          link.addEventListener('click', () => trackNavigatorEvent('demo_cta_click', { cta: cta.id }))
          return link
        })(),
      ]),
    )
  }
  if (!shown.length) {
    wrap.append(
      h('div', { class: 'rn-empty' }, [
        'No actionable triggers from the answers so far. Try adding more detail, or browse the register for the full library.',
      ]),
    )
  }
  for (const match of shown) {
    const record = PUBLISHED_RECORDS.find((item) => item.id === match.recordId)
    if (!record) continue
    wrap.append(
      h('article', { class: 'rn-card rn-match' }, [
        h('div', { class: 'rn-match__meta' }, [
          badge('rn-badge--in_force', APPLICABILITY_LABELS[match.state]),
          badge('rn-badge--obligation', OBLIGATION_LABELS[match.obligationLevel]),
        ]),
        h('h3', {}, [recordLink(record.slug, record.shortTitle)]),
        h('p', { class: 'rn-match__why' }, [h('strong', {}, ['Why it appears. ']), match.reasons[0] || '']),
        h('dl', { class: 'rn-match__facts' }, [
          h('div', {}, [
            h('dt', {}, ['Triggered by']),
            h('dd', {}, [match.triggeredBy.join('; ') || 'See the reason above.']),
          ]),
          h('div', {}, [
            h('dt', {}, ['Still unverified']),
            h('dd', {}, [match.unverified.join('; ') || 'None recorded beyond the official source.']),
          ]),
          h('div', {}, [
            h('dt', {}, ['Source']),
            h('dd', {}, [
              h('a', { href: match.sourceUrls[0] || '#', rel: 'noopener noreferrer' }, [
                match.sourceUrls[0] || 'Record',
              ]),
            ]),
          ]),
        ]),
      ]),
    )
  }
  return wrap
}

function mountChecklist(root: HTMLElement): void {
  hideSsr()
  const session = loadSession()
  if (!session) {
    root.append(h('div', { class: 'rn-empty' }, [
      'No checklist is stored in this browser tab. ',
      h('a', { href: `${BASE}/explorer` }, ['Answer the company profile']),
      ' to generate one. Nothing is uploaded.',
    ]))
    return
  }
  const host = h('div')
  const draw = () => {
    const current = loadSession() || session
    const hidden = new Set(current.hiddenIds)
    const done = new Set(current.completedIds)
    const visible = current.items.filter((item) => !hidden.has(item.id))
    host.replaceChildren()
    host.append(h('p', {}, [`Preliminary planning aid · ${visible.length} visible items · generated ${current.generatedAt}`]))
    host.append(h('p', {}, ['Marking an item complete records a planning note in this browser. It does not prove legal compliance.']))
    if (!visible.length) host.append(h('div', { class: 'rn-empty' }, ['Every item is hidden. Start over to restore them.']))
    for (const item of visible) {
      const box = h('input', { type: 'checkbox' }) as HTMLInputElement
      box.checked = done.has(item.id)
      box.addEventListener('change', () => {
        const next = loadSession() || current
        const set = new Set(next.completedIds)
        if (box.checked) set.add(item.id)
        else set.delete(item.id)
        next.completedIds = [...set]
        saveSession(next)
      })
      const hide = h('button', { type: 'button', class: 'btn btn--secondary btn--sm' }, ['Hide as irrelevant']) as HTMLButtonElement
      hide.addEventListener('click', () => {
        const next = loadSession() || current
        next.hiddenIds = [...new Set([...next.hiddenIds, item.id])]
        saveSession(next)
        draw()
      })
      host.append(h('article', { class: 'rn-check' }, [
        h('label', {}, [box, ` ${done.has(item.id) ? 'Marked complete' : 'Mark complete'}`]),
        badge('rn-badge--obligation', APPLICABILITY_LABELS[item.applicability]),
        h('h3', {}, [recordLink(item.slug, item.title)]),
        h('p', {}, [h('strong', {}, ['Why it may be relevant. ']), item.why]),
        h('p', {}, [h('strong', {}, ['Suggested action. ']), item.action]),
        h('p', {}, [`Suggested owner: ${item.owner}`]),
        h('p', {}, [`Deadline: ${item.deadlineLabel}`]),
        h('p', {}, ['Source: ', h('a', { href: item.sourceUrl, rel: 'noopener noreferrer' }, [item.sourceTitle])]),
        h('p', {}, [`Verification needed: ${item.verificationNeeded.join('; ') || 'Confirm against the official source.'}`]),
        hide,
      ]))
    }
    host.append(leadForm(current))
  }

  const printBtn = h('button', { type: 'button', class: 'btn btn--secondary rn-no-print' }, ['Print']) as HTMLButtonElement
  printBtn.addEventListener('click', () => window.print())
  const pdfBtn = h('button', { type: 'button', class: 'btn btn--primary rn-no-print' }, ['Download PDF']) as HTMLButtonElement
  pdfBtn.addEventListener('click', async () => {
    const current = loadSession() || session
    const exported = buildChecklistExport(current.items, {
      hiddenIds: current.hiddenIds,
      completedIds: current.completedIds,
      generatedOn: current.generatedAt,
    })
    const blob = renderChecklistPdf(exported)
    const url = URL.createObjectURL(blob)
    const anchor = h('a', { href: url, download: 'uae-regulatory-checklist.pdf' }) as HTMLAnchorElement
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
    trackNavigatorEvent('checklist_downloaded', { format: 'pdf', items: exported.items.length })
  })
  const textBtn = h('button', { type: 'button', class: 'btn btn--secondary rn-no-print' }, ['Download text']) as HTMLButtonElement
  textBtn.addEventListener('click', () => {
    const current = loadSession() || session
    const exported = buildChecklistExport(current.items, {
      hiddenIds: current.hiddenIds,
      completedIds: current.completedIds,
      generatedOn: current.generatedAt,
    })
    const blob = new Blob([checklistToPlainText(exported)], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const anchor = h('a', { href: url, download: 'uae-regulatory-checklist.txt' }) as HTMLAnchorElement
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
    trackNavigatorEvent('checklist_downloaded', { format: 'text', items: exported.items.length })
  })
  const reset = h('button', { type: 'button', class: 'btn btn--secondary rn-no-print' }, ['Start over']) as HTMLButtonElement
  reset.addEventListener('click', () => {
    clearSession()
    window.location.href = `${BASE}/explorer`
  })
  root.append(h('div', { class: 'rn-actions rn-no-print' }, [printBtn, pdfBtn, textBtn, reset]), host)
  draw()
}

function leadForm(session: NavigatorSession): HTMLElement {
  trackNavigatorEvent('lead_form_view')
  const form = h('form', { class: 'rn-form rn-card rn-no-print' })
  const status = h('p')
  const email = h('input', { type: 'email', required: 'required', autocomplete: 'email' }) as HTMLInputElement
  const company = h('input', { type: 'text', required: 'required', autocomplete: 'organization' }) as HTMLInputElement
  const name = h('input', { type: 'text', autocomplete: 'name' }) as HTMLInputElement
  const job = h('input', { type: 'text' }) as HTMLInputElement
  const phone = h('input', { type: 'tel' }) as HTMLInputElement
  const sector = h('input', { type: 'text' }) as HTMLInputElement
  const carbon = h('input', { type: 'checkbox' }) as HTMLInputElement
  const demo = h('input', { type: 'checkbox' }) as HTMLInputElement
  const processing = h('input', { type: 'checkbox', required: 'required', name: 'consent' }) as HTMLInputElement
  const marketing = h('input', { type: 'checkbox' }) as HTMLInputElement
  form.append(
    h('h2', {}, ['Optional: send a note to Ecops']),
    h('p', {}, ['You can keep using the checklist without this form. Submitting sends only the fields below. Your questionnaire answers stay in this browser and are not included.']),
    field('Work email', email),
    field('Company name', company),
    field('Name (optional)', name),
    field('Job title (optional)', job),
    field('Phone (optional)', phone),
    field('Sector (optional)', sector),
    h('label', {}, [carbon, ' I am interested in carbon accounting']),
    h('label', {}, [demo, ' I am interested in a sustainability reporting demonstration']),
    h('label', {}, [processing, ' I agree that Ecops may use these contact details to respond about this checklist and related sustainability services.']),
    h('label', {}, [marketing, ' I also agree to occasional product updates. This is optional and is not required to receive a reply.']),
    h('button', { type: 'submit', class: 'btn btn--primary' }, ['Request a follow-up']),
    status,
  )
  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    if (!processing.checked) {
      status.className = 'rn-error'
      status.textContent = 'Consent to be contacted is required.'
      processing.focus()
      return
    }
    status.className = ''
    status.textContent = 'Sending…'
    const result = await submitNavigatorLead({
      workEmail: email.value,
      companyName: company.value,
      name: name.value,
      jobTitle: job.value,
      phone: phone.value,
      sector: sector.value,
      interestCarbonAccounting: carbon.checked,
      interestReportingDemo: demo.checked,
      consentProcessing: processing.checked,
      consentMarketing: marketing.checked,
      checklistCount: session.items.length,
    })
    if (result.ok) {
      status.className = 'rn-ok'
      status.textContent = 'Sent. Your checklist is still available on this page.'
      trackNavigatorEvent('lead_form_success', { items: session.items.length })
      return
    }
    status.className = 'rn-error'
    status.textContent = result.error
  })
  return form
}

function field(label: string, control: HTMLElement): HTMLElement {
  const id = control.id || `f-${label.toLowerCase().replace(/\W+/g, '-')}`
  control.id = id
  const wrap = h('div', { class: 'rn-field' })
  const lab = h('label', { for: id }, [label])
  wrap.append(lab, control)
  return wrap
}

function select(id: string, options: [string, string][]): HTMLSelectElement {
  const node = h('select', { id }) as HTMLSelectElement
  for (const [value, label] of options) {
    const option = h('option', { value }, [label]) as HTMLOptionElement
    node.append(option)
  }
  return node
}

function mountOverviewToc(): void {
  const links = [...document.querySelectorAll<HTMLAnchorElement>('.rn-toc a[href^="#"]')]
  if (!links.length) return
  const sections = links
    .map((link) => {
      const id = link.getAttribute('href')?.slice(1)
      const section = id ? document.getElementById(id) : null
      return section ? { link, section } : null
    })
    .filter((item): item is { link: HTMLAnchorElement; section: HTMLElement } => Boolean(item))

  const setActive = (id: string) => {
    for (const { link, section } of sections) {
      link.classList.toggle('is-active', section.id === id)
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
      if (visible[0]?.target instanceof HTMLElement) setActive(visible[0].target.id)
    },
    { rootMargin: '-20% 0px -55% 0px', threshold: [0.1, 0.25, 0.5] },
  )
  for (const { section } of sections) observer.observe(section)

  for (const { link } of sections) {
    link.addEventListener('click', () => {
      const mobile = document.querySelector<HTMLDetailsElement>('.rn-toc__mobile')
      if (mobile && window.matchMedia('(max-width: 980px)').matches) mobile.open = false
    })
  }
}

function boot(): void {
  const page = document.getElementById('main-content')?.dataset.navigatorPage
  if (page) trackNavigatorEvent('regulatory_page_view', { page })
  if (page === 'overview') mountOverviewToc()
  document.addEventListener('click', (event) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.('a')
    if (!anchor) return
    const href = anchor.getAttribute('href') || ''
    if (!href.startsWith('http')) return
    try {
      const host = new URL(href).host
      if (!host.includes('ecopshub.com')) trackNavigatorEvent('official_source_click', { host })
    } catch {
      // Ignore malformed links.
    }
  })
  const root = document.getElementById('navigator-root')
  const which = root?.dataset.page || page
  if (which === 'timeline' && root) mountTimeline(root)
  else if (which === 'register' && root) mountRegister(root)
  else if (which === 'deadlines' && root) mountDeadlines(root)
  else if (which === 'explorer' && root) mountExplorer(root)
  else if (which === 'checklist' && root) mountChecklist(root)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot)
else boot()
